import { z } from 'zod';
/**
 * Tab Thiết kế (design control) — docs/plan-design-control.md.
 *
 * Đọc một file Figma, lưu **bản chụp** (JSON rút gọn + ảnh PNG từng màn hình trên
 * S3), so sánh hai bản chụp như git compare.
 *
 * ⚠️ Bản chụp **không** phải bản sao lưu mở lại được trong Figma: API Figma không
 * trả file `.fig`. Giao diện phải nói điều đó (§0.1).
 */
// ── Figma connection (cấp tổ chức) ──────────────────────────────────────────
/**
 * Token Figma của tổ chức — quyết định 1 (§12 sửa 4). Một PAT của người có ghế
 * **Full hoặc Dev**: ghế View chỉ có 20 lượt Tier 1 mỗi tháng, tức khoảng một lần
 * chụp. Token không bao giờ rời server; view chỉ có bốn ký tự cuối.
 */
export const FigmaConnectionView = z.object({
    id: z.uuid(),
    label: z.string(),
    tokenLast4: z.string(),
    /** `GET /v1/me` lúc lưu. `null` = chưa kiểm được. */
    figmaUserHandle: z.string().nullable(),
    verifiedAt: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const CreateFigmaConnectionInput = z.object({
    label: z.string().trim().min(1).max(100),
    /** Personal access token, scope `file_content:read` + `file_metadata:read` + `file_versions:read`. */
    token: z.string().trim().min(20).max(200),
    /**
     * Thử luôn một file (`GET /files/:key/meta`, Tier 3 — rẻ) — §12 sửa 4. Token
     * đúng mà không xem được file của đội thì biết ngay ở màn cấu hình, không phải
     * lúc bấm chụp lần đầu.
     */
    testFileUrl: z.url().max(2048).optional(),
});
/**
 * Kết quả kiểm token. Token bị Figma **từ chối** thì không được lưu (API trả
 * `FIGMA_FORBIDDEN`); còn Figma không trả lời được thì token vẫn lưu, chưa
 * `verifiedAt`, và kiểm lại được sau.
 */
export const FigmaTokenTest = z.object({
    ok: z.boolean(),
    figmaUserHandle: z.string().nullable(),
    /** Có khi gửi `testFileUrl` và file đọc được. */
    fileName: z.string().nullable(),
    /** Mã lỗi (`FIGMA_*`) của bước hỏng, nếu có. */
    errorCode: z.string().nullable(),
});
export const FigmaConnectionSaved = z.object({
    connection: FigmaConnectionView,
    test: FigmaTokenTest,
});
// ── File, bản chụp, màn hình ────────────────────────────────────────────────
/** Figma trả `role` của token trên file. `viewer` ⇒ cảnh báo hạn mức (§12 sửa 4). */
export const FigmaFileRole = z.enum(['owner', 'editor', 'viewer']);
export const LinkDesignFileInput = z.object({
    /** Link dán từ Figma: `https://www.figma.com/design/<key>/…?node-id=1-2`. */
    url: z.url().max(2048),
});
export const DesignFileView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    fileKey: z.string(),
    name: z.string(),
    /**
     * Chỉ chụp nút này (vd một section) thay vì cả file — `node-id` trong link.
     * Với token ghế View đây là cách duy nhất chụp được nhiều hơn một lần mỗi tháng.
     */
    scopeNodeId: z.string().nullable(),
    figmaRole: FigmaFileRole.nullable(),
    /** Phiên bản mới nhất thấy trên Figma (job quét `/meta`), có thể mới hơn bản chụp mới nhất. */
    latestRemoteVersion: z.string().nullable(),
    latestRemoteModifiedAt: z.iso.datetime().nullable(),
    lastCheckedAt: z.iso.datetime().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
/**
 * `partial` = vài ảnh render lỗi (Figma trả URL `null` cho màn hình đó) — lỗi
 * **từng màn hình**, không phải lỗi cả bản chụp (§1.1).
 */
export const DesignSnapshotStatus = z.enum([
    'queued',
    'fetching',
    'rendering',
    'ready',
    'partial',
    'failed',
]);
export const DesignSnapshotView = z.object({
    id: z.uuid(),
    designFileId: z.uuid(),
    figmaVersion: z.string(),
    figmaVersionLabel: z.string().nullable(),
    figmaVersionAuthor: z.string().nullable(),
    figmaLastModified: z.iso.datetime().nullable(),
    status: DesignSnapshotStatus,
    errorCode: z.string().nullable(),
    errorDetail: z.string().nullable(),
    pageCount: z.number().int(),
    screenCount: z.number().int(),
    /** Lượt Tier 1 đã tiêu — Figma không trả hạn mức còn lại, nên đây là nguồn duy nhất (§12). */
    tier1Calls: z.number().int(),
    imageScale: z.number(),
    requestedBy: z.uuid(),
    requestedAt: z.iso.datetime(),
    capturedAt: z.iso.datetime().nullable(),
});
/**
 * Lượt Tier 1 ước tính cho một lần chụp (§12 sửa 1) — phải hiện **trước** khi
 * người dùng bấm Chụp, vì với ghế View đó là gần hết hạn mức cả tháng.
 */
export const DesignCaptureEstimate = z.object({
    screenCount: z.number().int(),
    tier1Calls: z.number().int(),
});
export const LinkDesignFileResult = z.object({
    file: DesignFileView,
    estimate: DesignCaptureEstimate,
});
export const RequestDesignSnapshotInput = z.object({
    /** Phiên bản Figma (`GET /versions`). Bỏ trống = bản mới nhất lúc bấm. */
    version: z
        .string()
        .regex(/^\d{1,40}$/)
        .optional(),
});
/** §4 và §12 sửa 2. Chỉ `screen` hiện mặc định. */
export const DesignScreenKind = z.enum(['screen', 'component', 'fragment']);
export const DesignImageStatus = z.enum(['pending', 'ready', 'failed']);
/** Nguồn của một đoạn chữ người dùng đọc: mô tả màn hình, thuyết minh thay đổi. */
export const DesignTextStatus = z.enum(['ai_suggested', 'approved', 'manual']);
export const DesignFileDetail = z.object({
    file: DesignFileView,
    /** Mới nhất trước. */
    snapshots: z.array(DesignSnapshotView),
});
export const DesignScreenView = z.object({
    id: z.uuid(),
    snapshotId: z.uuid(),
    nodeId: z.string(),
    pageNodeId: z.string(),
    pageName: z.string(),
    groupPath: z.array(z.string()),
    name: z.string(),
    kind: DesignScreenKind,
    width: z.number().int(),
    height: z.number().int(),
    /** Cùng tên trong cùng nhóm: `Login · 3/15` (§12 sửa 3). */
    state: z.object({ index: z.number().int(), count: z.number().int() }).nullable(),
    contentHash: z.string(),
    imageStatus: DesignImageStatus,
    /** Ghi đè của người dùng, đi theo **file** qua mọi bản chụp. */
    hidden: z.boolean(),
    sortOrder: z.number().int(),
    /** Presigned GET (1 giờ). `null` khi ảnh render hỏng hoặc bản chụp chưa xong. */
    imageUrl: z.string().nullable(),
    /** Cây rút gọn (`.json.gz`) — nút "Tải JSON". */
    treeUrl: z.string().nullable(),
});
/** Thẻ file trong danh sách: file + bản chụp mới nhất, để hiện "Có bản mới trên Figma". */
export const DesignFileSummary = DesignFileView.extend({
    latestSnapshot: DesignSnapshotView.nullable(),
    /** Figma có phiên bản mới hơn bản chụp mới nhất (§7.3). */
    hasRemoteUpdate: z.boolean(),
});
export const DesignVersionView = z.object({
    id: z.string(),
    createdAt: z.iso.datetime(),
    /** Bản autosave không có nhãn (§12) — hiện thời điểm + người sửa. */
    label: z.string().nullable(),
    author: z.string().nullable(),
});
export const DesignVersionPage = z.object({
    versions: z.array(DesignVersionView),
    hasMore: z.boolean(),
});
/**
 * Mô tả khoá theo `contentHash`, không theo bản chụp: màn hình không đổi giữa hai
 * bản thì dùng lại mô tả đã duyệt, không tốn AI lần hai (§6).
 */
export const DesignScreenDescriptionView = z.object({
    id: z.uuid(),
    designFileId: z.uuid(),
    nodeId: z.string(),
    contentHash: z.string(),
    body: z.string(),
    status: DesignTextStatus,
    approvedBy: z.uuid().nullable(),
    approvedAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
});
export const WriteDesignScreenDescriptionInput = z.object({
    body: z.string().min(1).max(20_000),
});
export const SetDesignScreenHiddenInput = z.object({ hidden: z.boolean() });
// ── So sánh (§5) ────────────────────────────────────────────────────────────
/** Toạ độ trong màn hình, đơn vị px thiết kế (nhân `imageScale` để ra px ảnh). */
export const DesignBox = z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() });
export const DesignChangeKind = z.enum([
    'text_changed',
    'node_added',
    'node_removed',
    'style_changed',
    'layout_changed',
    'component_swapped',
    'visibility_changed',
]);
/** Câu mẫu không cần AI: khoá i18n `designControl.change.<kind>` + tham số. */
export const DesignAutoText = z.object({
    key: z.string(),
    params: z.record(z.string(), z.union([z.string(), z.number()])),
});
/** Một dòng thay đổi — đầu ra của `diffScreenTrees` (API, `modules/design/diff`). */
export const DesignChange = z.object({
    screenNodeId: z.string(),
    nodeId: z.string(),
    /** Tên từ dưới màn hình xuống tới chính node: `['Form', 'Nút chính']`. */
    nodePath: z.array(z.string()),
    kind: DesignChangeKind,
    /** `fills`, `fontSize`, `position`, `gap`, `parent`… `null` với kind là cả node. */
    property: z.string().nullable(),
    before: z.unknown(),
    after: z.unknown(),
    /** `null` ở phía node vắng mặt, và ở dòng đã gộp. */
    bboxBefore: DesignBox.nullable(),
    bboxAfter: DesignBox.nullable(),
    /** > 1 khi nhiều thay đổi giống hệt đã được gộp (§5.2). */
    count: z.number().int().positive(),
    autoText: DesignAutoText,
});
export const DesignScreenDiffStatus = z.enum([
    'added',
    'removed',
    'modified',
    'unchanged',
    'replaced',
]);
/**
 * Một màn hình trong bản so sánh. Đổi tên / đổi nhóm là **cờ** cạnh `status`, vì
 * một màn hình vừa đổi tên vừa đổi nội dung là chuyện thường (§13).
 */
export const DesignScreenDiff = z.object({
    status: DesignScreenDiffStatus,
    baseNodeId: z.string().nullable(),
    headNodeId: z.string().nullable(),
    name: z.string(),
    pageName: z.string(),
    groupPath: z.array(z.string()),
    renamedFrom: z.string().nullable(),
    movedFrom: z.object({ pageName: z.string(), groupPath: z.array(z.string()) }).nullable(),
    /** `replaced` là **đoán**; giao diện phải ghi "có thể là". */
    heuristic: z.boolean(),
    changes: z.array(DesignChange),
    /** Vượt trần thay đổi mỗi màn hình; giao diện nói ra, không cắt im lặng. */
    truncated: z.boolean(),
});
export const DesignDiffStats = z.object({
    added: z.number().int(),
    removed: z.number().int(),
    modified: z.number().int(),
    renamed: z.number().int(),
    moved: z.number().int(),
    replaced: z.number().int(),
    /** Tổng `count` — dòng đã gộp tính đúng số thay đổi nó che. */
    changes: z.number().int(),
});
export const DesignComparisonStatus = z.enum(['pending', 'ready', 'failed']);
export const CreateDesignComparisonInput = z.object({
    baseSnapshotId: z.uuid(),
    headSnapshotId: z.uuid(),
});
export const ExportLink = z.object({ url: z.string(), expiresInSec: z.number().int() });
// ── Bản so sánh đã lưu (DC-f) ───────────────────────────────────────────────
export const DesignComparisonView = z.object({
    id: z.uuid(),
    designFileId: z.uuid(),
    baseSnapshotId: z.uuid(),
    headSnapshotId: z.uuid(),
    status: DesignComparisonStatus,
    errorCode: z.string().nullable(),
    stats: DesignDiffStats.nullable(),
    summaryBody: z.string().nullable(),
    summaryStatus: DesignTextStatus.nullable(),
    computedAt: z.iso.datetime().nullable(),
});
/** Một dòng thay đổi đã lưu: dòng tất định + lời thuyết minh (nếu có). */
export const DesignChangeRow = DesignChange.extend({
    id: z.uuid(),
    narration: z.string().nullable(),
    narrationStatus: DesignTextStatus.nullable(),
});
export const DesignComparisonScreenRow = DesignScreenDiff.extend({
    id: z.uuid(),
    changes: z.array(DesignChangeRow),
    /** Presigned (1 giờ). `null` ở phía màn hình vắng mặt, hoặc ảnh render hỏng. */
    baseImageUrl: z.string().nullable(),
    headImageUrl: z.string().nullable(),
    /** Kích thước màn hình ở mỗi phía, để quy bbox ra phần trăm trên ảnh. */
    baseSize: z.object({ w: z.number(), h: z.number() }).nullable(),
    headSize: z.object({ w: z.number(), h: z.number() }).nullable(),
});
export const DesignComparisonDetail = z.object({
    comparison: DesignComparisonView,
    screens: z.array(DesignComparisonScreenRow),
});
// ── AI: mô tả màn hình (DC-h) ───────────────────────────────────────────────
/**
 * Output của skill `describe_design_screens` — khung cố định §8.2. Có cấu trúc
 * chứ không phải markdown tự do: `finalize` kiểm được từng màn hình, và server
 * dựng markdown một cách duy nhất khi áp dụng.
 */
export const DescribedDesignScreen = z.object({
    nodeId: z.string().min(1).max(100),
    /** Một câu: màn hình này để làm gì. */
    purpose: z.string().min(1).max(500),
    /** Thành phần chính theo vùng, trên → dưới. */
    parts: z.array(z.string().min(1).max(300)).min(1).max(20),
    actions: z.array(z.string().min(1).max(300)).max(20),
    states: z.array(z.string().min(1).max(300)).max(20),
});
export const DescribeDesignScreensOutput = z.object({
    screens: z.array(DescribedDesignScreen).max(20),
});
/** Áp dụng một gợi ý AI thành các mô tả `ai_suggested` — vẫn phải duyệt từng cái (luật #6). */
export const ImportDesignDescriptionsInput = z.object({ suggestionId: z.uuid() });
// ── AI: thuyết minh thay đổi (DC-i) ─────────────────────────────────────────
/**
 * Output của `narrate_design_changes` — §8.3. Mỗi `changeId` được giao có
 * **đúng một** lời thuyết minh; `finalize` loại id bịa và id lặp, và đòi đủ.
 */
export const NarrateDesignChangesOutput = z.object({
    /** Vài câu cho cả bản so sánh: thay đổi nào đáng chú ý với người dùng cuối. */
    summary: z.string().min(1).max(2000),
    changes: z
        .array(z.object({
        changeId: z.uuid(),
        /** 1–2 câu về **ý nghĩa** với người dùng cuối, không lặp lại giá trị (câu mẫu đã có). */
        text: z.string().min(1).max(600),
    }))
        .max(80),
});
/** Viết tay / sửa lời thuyết minh một thay đổi hoặc lời tổng kết. */
export const WriteDesignNarrationInput = z.object({ body: z.string().min(1).max(2000) });
//# sourceMappingURL=design.js.map